/**
 * My clients — the doula's own caseload.
 *
 * §3.14 asks for "her whole caseload in one place, not just the one client she
 * was most recently matched with". Until now the endpoint existed and nothing
 * rendered it: fifteen routes on her surface, one screen, and that one her own
 * profile.
 *
 * WHAT THE NUMBERS MEAN, AND WHY THEY ARE SEPARATED
 * Visits come in two kinds and conflating them was a real bug. The allowance is
 * SHARED across prenatal and postpartum — summing the per-activity caps would
 * promise her sixteen where the benefit allows eight. And care that
 * deliberately produces no claim is still care she gave, so it is shown, but it
 * does not come off the allowance. Before that split this screen would have told
 * her four visits remained while the member's own profile said five.
 *
 * Offers are deliberately NOT here. Answering a mother is its own decision with
 * its own consequences, and burying it in a list of people she already works
 * with is how it gets answered carelessly.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CalendarClock, Languages, Users } from 'lucide-react';

interface CaseloadEntry {
  member_id: number;
  name: string;
  preferred_language: string | null;
  role: string | null;
  since: string | null;
  due_date: string | null;
  visits_logged: number;
  visits_not_billable: number;
  /** Delivered by another organisation. The allowance is per pregnancy, not per
   *  provider, so these have already spent part of it. */
  visits_elsewhere: number;
  visits_remaining: number | null;
}

function when(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Weeks until her due date, or null when there isn't one. */
function weeksAway(due: string | null): number | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / (7 * 24 * 60 * 60 * 1000));
}

export default function DoulaCaseloadPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CaseloadEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await authFetchJson<CaseloadEntry[]>('/api/doula/v1/caseload'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.caseloadFailed', 'We could not load your clients.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <PageState variant="loading" />;

  if (error) {
    return (
      <PageContainer>
        <PageHeader title={t('doula.myClients', 'My clients')} />
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </PageContainer>
    );
  }

  const list = rows ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t('doula.myClients', 'My clients')}
        description={t(
          'doula.myClientsDescription',
          'Everyone you are working with, and how many visits each of them has left.',
        )}
      />

      {list.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('doula.noClients', 'You are not working with anyone yet.')}
          </p>
          {/* The empty state has to say which empty it is: a doula with no
              clients is usually one who has not been offered any, not one with
              a broken screen. */}
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'doula.noClientsHint',
              'When a mother chooses you, she appears here once you accept.',
            )}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {list.map((c) => {
            const weeks = weeksAway(c.due_date);
            return (
              <li key={c.member_id} className="rounded-md border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Link
                      to={`/doula/member/${c.member_id}`}
                      className="text-base font-medium hover:underline"
                    >
                      {c.name}
                    </Link>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                      {c.preferred_language && (
                        <span className="flex items-center gap-1">
                          <Languages className="h-3.5 w-3.5" />
                          {c.preferred_language}
                        </span>
                      )}
                      <span>
                        {t('doula.workingSince', 'with you since {{date}}', { date: when(c.since) })}
                      </span>
                      {c.role && <span>{c.role}</span>}
                    </p>
                  </div>

                  {c.due_date && (
                    <div className="text-right">
                      <p className="flex items-center justify-end gap-1 text-sm font-medium">
                        <CalendarClock className="h-4 w-4" />
                        {when(c.due_date)}
                      </p>
                      {/* A date she has to subtract from today is a date she
                          will get wrong at 3am. The weeks are the useful part. */}
                      {weeks !== null && weeks >= 0 && (
                        <p className="text-xs text-muted-foreground">
                          {t('doula.weeksAway', '{{count}} weeks away', { count: weeks })}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3 text-sm">
                  <span className="font-medium">
                    {t('doula.visitsLogged', '{{count}} visits logged', { count: c.visits_logged })}
                  </span>
                  {c.visits_remaining !== null && (
                    <Badge variant={c.visits_remaining === 0 ? 'danger' : 'secondary'}>
                      {t('doula.visitsLeft', '{{count}} left', { count: c.visits_remaining })}
                    </Badge>
                  )}
                  {/* Shown only when there is one, because for most clients
                      there will not be, and a permanent "0 not billable" would
                      make the ordinary case look like it had a caveat. */}
                  {/* Shown whenever it is non-zero, because without it a
                      smaller "left" count reads as a bug in her own screen
                      rather than as care someone else has already given. */}
                  {c.visits_elsewhere > 0 && (
                    <Badge variant="secondary">
                      {t('doula.visitsElsewhere', '{{count}} elsewhere', {
                        count: c.visits_elsewhere,
                      })}
                    </Badge>
                  )}
                  {c.visits_not_billable > 0 && (
                    <span className="text-muted-foreground">
                      {t('doula.notBillable', '{{count}} not billable', {
                        count: c.visits_not_billable,
                      })}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {list.length > 0 && (
        <p className="mt-4 text-xs text-muted-foreground">
          {t(
            'doula.sharedAllowanceFootnote',
            'Visits left come from the shared prenatal and postpartum allowance, not a separate count for each. Care that does not bill is still recorded and does not come off it.',
          )}
        </p>
      )}
    </PageContainer>
  );
}
